/**
 * Why a moderator's voice room could not go on — the private call's, or the
 * voice monitor's — in the moderator's words, and where the room stands.
 *
 * LiveKit says it in English, by its own names, and the call's window wrote
 * the message out as it came — "could not establish signal connection: Failed
 * to fetch" — in every language, while the monitor said "Connecting..." for
 * ever. The message goes to the log; each window says one of a few things: the
 * voice server is out of reach, it refused the connection, or the connection
 * dropped — and that the connection failed, for anything else.
 *
 * A microphone the browser will not give is not one of them: the call goes on
 * without it, the moderator unheard, as the game client's call does — a
 * warning, and the room's state left as it is.
 */

import {
  ConnectionError,
  ConnectionErrorReason,
  DeviceUnsupportedError,
  DisconnectReason,
  MediaDeviceFailure,
} from 'livekit-client';

/** A room that cannot go on: the voice server out of reach, the connection refused, or dropped, or a failure the windows have no words of their own for. */
export type RoomFailure = 'unreachable' | 'refused' | 'dropped' | 'other';

/** Each failure's words, keys written whole: one namespace, which both windows read. */
export const ROOM_FAILURE_KEY: Record<RoomFailure, string> = {
  unreachable: 'voiceRoom.failure.unreachable',
  refused: 'voiceRoom.failure.refused',
  dropped: 'voiceRoom.failure.dropped',
  other: 'voiceRoom.failure.other',
};

/**
 * What a LiveKit error says of the room. Out of reach: no answer, the socket
 * failed, the wait ran out, or the connection could not be made — LiveKit's
 * internal error is the media path that never opened, or a socket closed as
 * it opened. Refused: the server answered no — the token not allowed, or no
 * such service. Dropped: the server sent the moderator away. Null when there
 * is nothing to say: the connection was cancelled, by the window closing as it
 * connected — or the error is the microphone's (microphoneFailure), which the
 * call goes on through.
 */
export function roomFailure(error: unknown): RoomFailure | null {
  if (error instanceof ConnectionError) {
    // instanceof gives ConnectionError<any>: its reason is named here, so the switch can place every one.
    const reason: ConnectionErrorReason = error.reason;
    switch (reason) {
      case ConnectionErrorReason.ServerUnreachable:
      case ConnectionErrorReason.WebSocket:
      case ConnectionErrorReason.Timeout:
      case ConnectionErrorReason.InternalError:
        return 'unreachable';
      case ConnectionErrorReason.NotAllowed:
      case ConnectionErrorReason.ServiceNotFound:
        return 'refused';
      case ConnectionErrorReason.LeaveRequest:
        return 'dropped';
      case ConnectionErrorReason.Cancelled:
        return null;
      default:
        return unplacedReason(reason);
    }
  }
  return microphoneFailure(error) ? null : 'other';
}

/**
 * Every reason LiveKit names is placed above, so one a LiveKit upgrade adds
 * fails the build here. At run time, a reason this build does not know says
 * only that the connection failed.
 */
function unplacedReason(_reason: never): RoomFailure {
  return 'other';
}

/**
 * Whether a LiveKit error is the microphone's: the browser refused it, has
 * none, cannot open it, or cannot reach one at all. getFailure names any error
 * that has a name, so only its three device answers count.
 */
export function microphoneFailure(error: unknown): boolean {
  if (error instanceof DeviceUnsupportedError) return true;
  // getFailure reads a name with `in`, which throws on anything not an object.
  if (typeof error !== 'object' || error === null) return false;
  switch (MediaDeviceFailure.getFailure(error)) {
    case MediaDeviceFailure.PermissionDenied:
    case MediaDeviceFailure.NotFound:
    case MediaDeviceFailure.DeviceInUse:
      return true;
    default:
      return false;
  }
}

/**
 * What a connected room's end says: nothing when the moderator ended it, and
 * otherwise that its connection dropped — the server shut down, the room
 * went, the moderator was removed, or the line was lost.
 */
export function disconnectFailure(reason: DisconnectReason | undefined): RoomFailure | null {
  return reason === DisconnectReason.CLIENT_INITIATED ? null : 'dropped';
}

/** Where a room stands: the failure is set with the 'error' state, and null in every other. */
export interface RoomLink {
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error';
  failure: RoomFailure | null;
}

/** What LiveKit tells of a room: it connected, it went (and why), or it failed. */
type RoomLinkEvent =
  | { type: 'connected' }
  | { type: 'disconnected'; reason?: DisconnectReason }
  | { type: 'failed'; failure: RoomFailure };

/**
 * Where a room stands after what LiveKit tells, whatever order its events come
 * in. Connected forgets a failure. A room that was connected and goes by no
 * hand of the moderator's has dropped, said so; one that failed keeps what it
 * failed with — the same object, so nothing renders again for it; any other
 * has simply ended.
 */
export function roomLinkAfter(link: RoomLink, event: RoomLinkEvent): RoomLink {
  switch (event.type) {
    case 'connected':
      return { connectionState: 'connected', failure: null };
    case 'failed':
      return { connectionState: 'error', failure: event.failure };
    case 'disconnected': {
      if (link.connectionState === 'error') return link;
      const dropped = link.connectionState === 'connected' ? disconnectFailure(event.reason) : null;
      return dropped ? { connectionState: 'error', failure: dropped } : { connectionState: 'disconnected', failure: null };
    }
  }
}
